import {Navigate, Route, Routes, useLocation} from 'react-router-dom'
import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { LocaleContext, LoginContext, NavigateLockContext, OverlayContext, ScrollContext, StateContext } from '../common/App';
import { BurgerButtonBig, CloseButtonBig, ErrorComp, HeaderBig, HeaderSmall, HeaderWithButton, LangSelection, Loading, NavigateSmart } from '../common/Common';
import './DashboardApp.css';
import { compareMediationStatus, sanitizeLocaleMessages, showMatch } from '../common/util';
import { DashboardOverview } from './DashboardOverview';
import { Profile } from './Profile';
import { Match } from './Match';
import { ConsumerMenu } from '../ConsumerMenu';
import { LoadState, MediationStatus } from '../common/enums';
import { ConsumerContext } from '..';
import { useAutoLoad, useNavigateSmart } from '../common/hooks';

export function DashboardApp(props) {
    const locale = useContext(LocaleContext);
    const state = useContext(StateContext);
    const scroll = useContext(ScrollContext);
    const login = useContext(LoginContext);
    const consumer = useContext(ConsumerContext);
    const overlay = useContext(OverlayContext);
    const navigateLock = useContext(NavigateLockContext);

    const location = useLocation();
    const navigate = useNavigateSmart();

    const dashboardAppRef = useRef();
    const tabBoxRef = useRef();

    const localeRef = useRef();

    // const [currentTabIdx, setCurrentTabIdx] = useState(0);

    const [userData, setUserData] = useState(null);

    const [menuPosRight, setMenuPosRight] = useState(0);

    const [tabBoxWidth, setTabBoxWidth] = useState(0);
    const [endDummyHeight, setEndDummyHeight] = useState(0);

    const [locLoadState, setLocLoadState] = useState(LoadState.LOADING);


    // For Menu
    const menuRef = useRef();
    const menuBtnRef = useRef();
    const [menuVisible, setMenuVisible] = useState(false);


    // FUNCTIONS:

    function handleMenuClick(e) {
        if(menuVisible) {
            setMenuVisible(false);
        }
        else {
            setMenuVisible(true);
        }
    }

    function handleTabChange(idx) {
        // setCurrentTabIdx(idx);

        if(idx == 0) {
            navigate("/dashboard");
        }
        else if(idx == 1) {
            navigate("profile");
        }
        else if(idx == 2) {
            navigate("match");
        }
    }


    // EFFECTS:

    useLayoutEffect(() => {
        localeRef.current = locale.locale;
    }, [locale.locale]);

    useLayoutEffect(() => {
        setLocLoadState(LoadState.LOADING);
        overlay.incrLoadingCounter();

        async function loadLocaleData(localeStr) {
          const messages = await import(`./i18n/${localeStr}.json`);
          locale.setMsgs((curMsgs) => Object.assign(sanitizeLocaleMessages(messages), curMsgs));
          setLocLoadState(LoadState.LOAD_SUCCESS);
          overlay.decrLoadingCounter();
        }

        loadLocaleData(locale.locale);
    }, []);  // Only execute this initially as App does not render this component while loading a new locale

    const [loadState, setLoadState] = useAutoLoad((json) => {
        // On load success
        setUserData(json);

        // setLoadState(LoadState.LOADING); // For testing
    }, () => {
        // On load error
    }, () => {
        // On before load

        if(consumer.declined || !consumer.mediationStatus || consumer.mediationStatus === MediationStatus.NONE ||
                consumer.mediationStatus === MediationStatus.DATA_COLLECTED) {
            return null;
        }

        return "/user/get_data";
    }, [], true, false, false, false, true);

    useLayoutEffect(() => {
        scroll.scrollToPlain(0, 1);
    }, [location.pathname]);

    useLayoutEffect(() => {
        function perform() {
            if(dashboardAppRef && dashboardAppRef.current) {
                setTabBoxWidth(dashboardAppRef.current.clientWidth - 1);
            }

            if(tabBoxRef && tabBoxRef.current) {
                setEndDummyHeight(tabBoxRef.current.clientHeight);
            }
        }

        perform();

        window.addEventListener("resize", perform);
        return () => window.removeEventListener("resize", perform);
    }, [loadState]);

    useLayoutEffect(() => {
        function handleClickAnywhere(e) {
            if (menuRef.current && !menuRef.current.contains(e.target) && menuBtnRef.current && !menuBtnRef.current.contains(e.target)) {
                setMenuVisible(false);
            }  
        }

        document.addEventListener("click", handleClickAnywhere);
        return () => document.removeEventListener("click", handleClickAnywhere);
    }, [menuRef, menuBtnRef]);

    useLayoutEffect(() => {
        scroll.scrollOffset.current = 85;

        return () => { scroll.scrollOffset.current = 0; };
    }, []);


    // RENDER:

    if(locLoadState !== LoadState.LOAD_SUCCESS) {
        return null;
    }

    let currentTabIdx = -1;

    if(location.pathname === "/dashboard") {
        currentTabIdx = 0;
    }
    else if(location.pathname === "/dashboard/profile") {
        currentTabIdx = 1;
    }
    else if(location.pathname === "/dashboard/match") {
        currentTabIdx = 2;
    }

    let noOfTabs = 2;

    if(showMatch(consumer)) {
        noOfTabs = 3;
    }

    return(
        <div ref={dashboardAppRef} id="dashboard_app">
            <div ref={menuRef}>
                <ConsumerMenu menuVisible={menuVisible} onMenuClick={handleMenuClick}/>
            </div>

            <div className="dashboard_content_box">
                <HeaderWithButton small={window.innerWidth <= 449} onClick={handleMenuClick} menuBtnRef={menuBtnRef} />

                { loadState === LoadState.LOADING ?
                    null
                :
                    loadState === LoadState.LOAD_SUCCESS ?
                        <Routes>
                            <Route path="/" element={ <DashboardOverview userData={userData} />}></Route>
                            <Route path="profile" element={ <Profile userData={userData} />}></Route>

                            { showMatch(consumer) ?
                                <Route path="match" element={ <Match userData={userData} />}></Route>
                            :
                                <Route path="match" element={ <NavigateSmart to="/dashboard" />}></Route>
                            }
                            
                        </Routes>
                    :
                        loadState === LoadState.LOAD_ERROR ?
                            <div className="side_padding">
                                <ErrorComp text={locale.msgs.GENERAL_ERROR_DESCR} />
                            </div>
                        :
                            null
                }

                <div style={{width: "100%", height: endDummyHeight}}></div>
            </div>

            <div ref={tabBoxRef} className="dashboard_tab_box noselect" style={{width: tabBoxWidth, gridTemplateColumns: "0.15fr repeat(" + noOfTabs + ", 1fr) 0.15fr"}}>
                    
                <img className="dashboard_tab_img" src={ currentTabIdx === 0 ? "/customer/icon_dashboard-mn-family-enabled.svg" : "/customer/icon_dashboard-mn-family-disabled.svg"}
                        onClick={(e) => navigateLock.handleLockProtectedClick(e, (e) => handleTabChange(0))}
                        style={{gridColumn: 2}}></img>
                <div className="dashboard_tab_name text_pink_tiny"
                        onClick={(e) => navigateLock.handleLockProtectedClick(e, (e) => handleTabChange(0))}
                        style={{gridColumn: 2}}>{locale.msgs.DASHBOARD_CONSUMER}</div>

                <img className="dashboard_tab_img" src={ currentTabIdx === 1 ? "/customer/icon_profile-enabled.svg" : "/customer/icon_profile-disabled.svg"}
                        onClick={(e) => navigateLock.handleLockProtectedClick(e, (e) => handleTabChange(1))}
                        style={{gridColumn: 3}}></img>
                <div className="dashboard_tab_name text_pink_tiny"
                        onClick={(e) => navigateLock.handleLockProtectedClick(e, (e) => handleTabChange(1))}
                        style={{gridColumn: 3}}>{locale.msgs.PROFILE}</div>

                { noOfTabs === 3 ?
                        <img className="dashboard_tab_img" src={ currentTabIdx === 2 ? "/common/icon_matches-enabled.svg" : "/common/icon_matches-disabled.svg"} id="match_icon"
                                onClick={(e) => navigateLock.handleLockProtectedClick(e, (e) => handleTabChange(2))}
                                style={{gridColumn: 4}}></img>
                :
                    null
                }

                { noOfTabs === 3 ?
                        <div className="dashboard_tab_name text_pink_tiny"
                                onClick={(e) => navigateLock.handleLockProtectedClick(e, (e) => handleTabChange(2))}
                                style={{gridColumn: 4}}>{locale.msgs.MATCH}</div>
                :
                    null
                }
                
            </div>

            { consumer.mediationStatus === MediationStatus.NONE && <NavigateSmart to="/collect_data/sensitive_data" /> }
            { consumer.mediationStatus === MediationStatus.DATA_COLLECTED && <NavigateSmart to="/be_patient" /> }
        </div>
    );
}
